import React, { useMemo, useState } from 'react';
import { Card, Table, Button, Input, Space, Tag, message, Modal, Select, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  ExportOutlined,
  EyeOutlined,
  GoldOutlined,
} from '@ant-design/icons';
import assetService, { type AssetDto } from '@/services/asset.service';
import pmvService from '@/services/pmv.service';
import { useAuthStore } from '@/store/authStore';

const ASSETS_QK = ['assets'];

const STATUS_COLOR: Record<string, string> = {
  draft: 'default',
  active: 'green',
  in_use: 'blue',
  under_maintenance: 'orange',
  idle: 'gold',
  disposed: 'red',
};

function downloadFilteredListAsCsv(assets: AssetDto[], filename: string) {
  const header = [
    'asset_code',
    'asset_name',
    'status',
    'serial_number',
    'barcode',
    'current_book_value',
    'asset_category',
    'site',
    'building',
    'room',
  ];
  const esc = (v: string | number | null | undefined) => {
    if (v == null) {
      return '';
    }
    const t = String(v);
    if (/[",\n\r]/.test(t)) {
      return `"${t.replace(/"/g, '""')}"`;
    }
    return t;
  };
  const lines: string[] = [header.join(',')];
  for (const a of assets) {
    lines.push(
      [
        esc(a.asset_code),
        esc(a.asset_name),
        esc(a.status),
        esc(a.serial_number),
        esc(a.barcode),
        a.current_book_value == null ? '' : String(a.current_book_value),
        esc(a.asset_category),
        esc(a.site),
        esc(a.building),
        esc(a.room),
      ].join(',')
    );
  }
  const body = lines.join('\r\n');
  const blob = new Blob([`\uFEFF${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const AssetsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canCreate = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.create'));
  const canView = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.view'));
  const canManagePmv = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('pmv.profile.manage') ||
      user?.permissions?.includes('pmv.admin') ||
      user?.permissions?.includes('pmv.manage')
  );
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [fleetFilter, setFleetFilter] = useState<'all' | 'in' | 'out'>('all');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [...ASSETS_QK, q, status],
    queryFn: () => assetService.list({ q: q || undefined, status: status || undefined }),
  });

  const fleetQ = useQuery({
    queryKey: ['pmv-profiles-for-assets-list'],
    queryFn: () => pmvService.listProfiles({ pageSize: 200 }),
    enabled: canManagePmv || canView,
    staleTime: 60_000,
  });

  const fleetByAssetId = useMemo(() => {
    const m = new Map<string, { id: string; operational_status: string }>();
    for (const p of fleetQ.data?.data ?? []) {
      if (p.asset_id) m.set(p.asset_id, { id: p.id, operational_status: p.operational_status });
    }
    return m;
  }, [fleetQ.data?.data]);

  const filteredRows = useMemo(() => {
    if (fleetFilter === 'all') return rows;
    return rows.filter((r) => {
      const inFleet = fleetByAssetId.has(r.id);
      return fleetFilter === 'in' ? inFleet : !inFleet;
    });
  }, [rows, fleetFilter, fleetByAssetId]);

  const delMut = useMutation({
    mutationFn: (id: string) => assetService.remove(id),
    onSuccess: () => {
      message.success('Asset removed');
      void queryClient.invalidateQueries({ queryKey: ASSETS_QK });
    },
    onError: (e: Error) => message.error(e.message || 'Delete failed'),
  });

  const importMut = useMutation({
    mutationFn: (file: File) => assetService.importFromCsv(file),
    onSuccess: (res) => {
      const { created, updated, errors, skipped } = res;
      if (errors?.length) {
        const preview = errors.slice(0, 5).join(' ');
        message.warning(
          `Created ${created}, updated ${updated}, skipped ${skipped}. ${errors.length} issue(s). ${preview}${
            errors.length > 5 ? '…' : ''
          }`
        );
      } else {
        message.success(`Import done: created ${created}, updated ${updated}${skipped ? `, skipped ${skipped}` : ''}`);
      }
      void queryClient.invalidateQueries({ queryKey: ASSETS_QK });
    },
    onError: (e: Error) => message.error(e.message || 'Import failed'),
  });

  const askDelete = (row: AssetDto) => {
    Modal.confirm({
      title: 'Remove this asset?',
      content: row.asset_name,
      okType: 'danger',
      onOk: () => delMut.mutateAsync(row.id),
    });
  };

  const columns: ColumnsType<AssetDto> = useMemo(
    () => [
      { title: 'Code', dataIndex: 'asset_code', key: 'asset_code', width: 120 },
      { title: 'Name', dataIndex: 'asset_name', key: 'asset_name', ellipsis: true },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (s: string) => <Tag color={STATUS_COLOR[s] || 'default'}>{s.replace(/_/g, ' ')}</Tag>,
      },
      {
        title: 'Fleet',
        key: 'fleet',
        width: 200,
        render: (_: unknown, r) => {
          const p = fleetByAssetId.get(r.id);
          if (p) {
            return (
              <Space size={4} wrap>
                <Tag color="blue" icon={<GoldOutlined />}>
                  In fleet
                </Tag>
                <Tag>{p.operational_status.replace(/_/g, ' ')}</Tag>
              </Space>
            );
          }
          if (r.status === 'disposed') return <Tag>N/A</Tag>;
          return <Tag>Not in fleet</Tag>;
        },
      },
      { title: 'Serial', dataIndex: 'serial_number', key: 'serial_number', width: 120 },
      {
        title: 'Book value',
        dataIndex: 'current_book_value',
        key: 'current_book_value',
        width: 120,
        render: (v: number | null) => (v != null ? Number(v).toLocaleString() : '—'),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 280,
        fixed: 'right',
        render: (_: unknown, r) => {
          const p = fleetByAssetId.get(r.id);
          return (
            <Space wrap size={0}>
              <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/asset/assets/${r.id}`)}>
                View
              </Button>
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => navigate(`/asset/assets/${r.id}/edit`)}>
                Edit
              </Button>
              {p ? (
                <Button type="link" size="small" onClick={() => navigate(`/asset/pmv/${p.id}`)}>
                  Passport
                </Button>
              ) : canManagePmv && r.status !== 'disposed' ? (
                <Button type="link" size="small" onClick={() => navigate(`/asset/assets/${r.id}/pmv/activate`)}>
                  Add to Fleet
                </Button>
              ) : null}
              <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => askDelete(r)}>
                Remove
              </Button>
            </Space>
          );
        },
      },
    ],
    [navigate, fleetByAssetId, canManagePmv]
  );

  return (
    <Card title="Assets">
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Search code, name, serial, barcode"
          style={{ width: 300 }}
          onSearch={(v) => setQ(v)}
        />
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 200 }}
          value={status}
          onChange={setStatus}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'active', label: 'Active' },
            { value: 'in_use', label: 'In use' },
            { value: 'under_maintenance', label: 'Under maintenance' },
            { value: 'idle', label: 'Idle' },
            { value: 'disposed', label: 'Disposed' },
          ]}
        />
        <Select
          style={{ width: 180 }}
          value={fleetFilter}
          onChange={setFleetFilter}
          options={[
            { value: 'all', label: 'All fleet status' },
            { value: 'in', label: 'In Fleet Register' },
            { value: 'out', label: 'Not in fleet' },
          ]}
        />
        <Button onClick={() => navigate('/asset/pmv')}>Open Fleet Register</Button>
        {canView && (
          <Button
            icon={<DownloadOutlined />}
            onClick={async () => {
              try {
                await assetService.downloadRegisterReportAsFile();
                message.success('Register downloaded');
              } catch (e: unknown) {
                message.error(e instanceof Error ? e.message : 'Download failed');
              }
            }}
          >
            Export register (CSV)
          </Button>
        )}
        {canView && (
          <Button
            icon={<ExportOutlined />}
            onClick={() => {
              if (!filteredRows.length) {
                message.info('No rows to export for the current filter');
                return;
              }
              const safe = (status || 'all').replace(/[^\w-]+/g, '-');
              downloadFilteredListAsCsv(filteredRows, `assets-list-${safe}.csv`);
              message.success('List exported');
            }}
          >
            Export this list (CSV)
          </Button>
        )}
        {canCreate && (
          <Button
            icon={<DownloadOutlined />}
            onClick={async () => {
              try {
                await assetService.downloadImportTemplateAsFile();
                message.success('Template downloaded');
              } catch (e: unknown) {
                message.error(e instanceof Error ? e.message : 'Download failed');
              }
            }}
          >
            Import template
          </Button>
        )}
        {canCreate && (
          <Upload
            accept=".csv,text/csv"
            showUploadList={false}
            beforeUpload={(file) => {
              void importMut.mutateAsync(file);
              return false;
            }}
          >
            <Button icon={<UploadOutlined />} loading={importMut.isPending}>
              Import CSV
            </Button>
          </Upload>
        )}
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/asset/assets/new')}>
          New asset
        </Button>
      </Space>
      <Table<AssetDto>
        rowKey="id"
        loading={isLoading || fleetQ.isLoading}
        dataSource={filteredRows}
        columns={columns}
        scroll={{ x: 1100 }}
        onRow={(r) => ({
          onDoubleClick: () => navigate(`/asset/assets/${r.id}`),
        })}
      />
    </Card>
  );
};
